import { MapTrifold, Crosshair, UsersThree, Storefront, Buildings } from "@phosphor-icons/react";
import { getLsmColor, FACILITY_COLORS, TRADE_ZONE_COLORS } from "./mapConstants";

export function SpatialTab({ scoutLayers }) {
  if (!scoutLayers) {
    return (
      <div className="flex-1 overflow-auto p-6 bg-slate-50" data-testid="spatial-tab-content">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <MapTrifold size={64} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Run a valuation to see spatial intelligence</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6 bg-slate-50" data-testid="spatial-tab-content">
      <div className="space-y-6">
        <div className="bg-white border border-slate-200 p-6 rounded-lg" data-testid="spatial-intelligence-panel">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <MapTrifold size={20} className="text-teal-600" />
            Spatial Intelligence
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Huff Gravity Model Card */}
            {scoutLayers.huff_model && (
              <div className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-lg p-4 border border-cyan-100" data-testid="huff-detail-card">
                <div className="flex items-center gap-2 mb-2">
                  <Crosshair size={16} className="text-cyan-600" />
                  <h4 className="text-sm font-bold text-slate-700">Huff Gravity Model</h4>
                </div>
                <div className="text-3xl font-black text-cyan-700 mb-1">{scoutLayers.huff_model.market_share_pct}%</div>
                <div className="text-xs text-slate-500 mb-3">Predicted Market Share</div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Capture Probability</span><span className="font-semibold text-slate-700">{(scoutLayers.huff_model.capture_probability * 100).toFixed(1)}%</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Cannibalization Risk</span><span className={`font-semibold ${scoutLayers.huff_model.cannibalization_risk === 'very_low' || scoutLayers.huff_model.cannibalization_risk === 'low' ? 'text-emerald-600' : scoutLayers.huff_model.cannibalization_risk === 'moderate' ? 'text-amber-600' : 'text-red-600'}`}>{scoutLayers.huff_model.cannibalization_risk.replace(/_/g, ' ')}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Est. Monthly Revenue</span><span className="font-bold text-emerald-600">R{(scoutLayers.huff_model.estimated_monthly_revenue_zar || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Monthly Market Size</span><span className="text-slate-700">R{(scoutLayers.huff_model.monthly_market_size_zar || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Competitors</span><span className="text-slate-700">{scoutLayers.huff_model.competitors_analyzed} analyzed</span></div>
                </div>
                <p className="text-xs text-slate-400 mt-2 italic">{scoutLayers.huff_model.interpretation}</p>
                {scoutLayers.huff_model.top_competitors?.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-cyan-100">
                    <div className="text-xs font-semibold text-slate-500 mb-1">Top Competitors:</div>
                    {scoutLayers.huff_model.top_competitors.map((c, i) => (
                      <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-slate-600 truncate mr-2">{c.name}</span><span className="text-slate-500 whitespace-nowrap">{c.individual_share}% | {c.distance_km}km</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Trade Area Card */}
            {scoutLayers.trade_area && (
              <div className="bg-gradient-to-br from-slate-50 to-emerald-50 rounded-lg p-4 border border-emerald-100" data-testid="trade-area-card">
                <div className="flex items-center gap-2 mb-2">
                  <MapTrifold size={16} className="text-emerald-600" />
                  <h4 className="text-sm font-bold text-slate-700">Walk-Time Trade Area</h4>
                </div>
                <div className="text-xs text-slate-500 mb-2">Terrain: {scoutLayers.trade_area.terrain.replace(/_/g, ' ')} | Walk Speed: {scoutLayers.trade_area.walk_speed_kmh} km/h</div>
                <div className="space-y-2">
                  {scoutLayers.trade_area.zones.map((z, i) => (
                    <div key={i} className={`flex items-center gap-2 p-2 rounded ${z.zone_label === 'primary' ? 'bg-emerald-50 border border-emerald-200' : z.zone_label === 'secondary' ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="w-2 h-2 rounded-full" style={{background: TRADE_ZONE_COLORS[z.zone_label]?.color || '#6b7280'}}></div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-slate-700">{z.minutes}-min walk ({(z.radius_km * 1000).toFixed(0)}m)</div>
                        <div className="text-xs text-slate-500">Pop: {z.estimated_population.toLocaleString()} | {z.spaza_clusters_inside} clusters | {(z.capture_pct * 100).toFixed(0)}% capture</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LSM & Demographics Card */}
            {scoutLayers.lsm?.nearest_ward && (
              <div className="bg-gradient-to-br from-slate-50 to-violet-50 rounded-lg p-4 border border-violet-100" data-testid="lsm-detail-card">
                <div className="flex items-center gap-2 mb-2">
                  <UsersThree size={16} className="text-violet-600" />
                  <h4 className="text-sm font-bold text-slate-700">LSM Demographics</h4>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-black" style={{background: getLsmColor(scoutLayers.lsm.nearest_ward.lsm_band)}}>{scoutLayers.lsm.nearest_ward.lsm_band}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-700">{scoutLayers.lsm.nearest_ward.name}</div>
                    <div className="text-xs text-slate-500">LSM Band {scoutLayers.lsm.nearest_ward.lsm_band}/10</div>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Avg Household Income</span><span className="font-semibold text-slate-700">R{scoutLayers.lsm.nearest_ward.avg_household_income.toLocaleString()}/mo</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Population</span><span className="text-slate-700">{scoutLayers.lsm.nearest_ward.population.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Households</span><span className="text-slate-700">{scoutLayers.lsm.nearest_ward.households.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Unemployment</span><span className="text-slate-700">{scoutLayers.lsm.nearest_ward.unemployment_rate}%</span></div>
                </div>
                <div className="mt-3 pt-2 border-t border-violet-100 text-xs">
                  <div className="text-slate-500 mb-1">Area Summary ({scoutLayers.lsm.wards_in_range.length} wards in range)</div>
                  <div className="flex justify-between"><span className="text-slate-500">Avg LSM Band</span><span className="font-semibold text-slate-700">{scoutLayers.lsm.avg_lsm_band}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Total Population</span><span className="text-slate-700">{scoutLayers.lsm.total_population.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Avg Income</span><span className="text-slate-700">R{scoutLayers.lsm.avg_household_income.toLocaleString()}</span></div>
                </div>
              </div>
            )}

            {/* Competitor Summary Card */}
            {scoutLayers.competitors && (
              <div className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-lg p-4 border border-orange-100" data-testid="competitor-summary-card">
                <div className="flex items-center gap-2 mb-2">
                  <Storefront size={16} className="text-orange-600" />
                  <h4 className="text-sm font-bold text-slate-700">Competitor Landscape</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-center p-2 bg-orange-100 rounded"><div className="text-lg font-black text-orange-700">{scoutLayers.competitors.total_spaza_clusters}</div><div className="text-xs text-orange-600">Spaza Clusters</div></div>
                  <div className="text-center p-2 bg-orange-100 rounded"><div className="text-lg font-black text-orange-700">{scoutLayers.competitors.total_estimated_shops}</div><div className="text-xs text-orange-600">Est. Shops</div></div>
                </div>
                {scoutLayers.competitors.spaza_clusters?.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-slate-500">Nearby Clusters:</div>
                    {scoutLayers.competitors.spaza_clusters.map((s, i) => (
                      <div key={i} className="flex justify-between text-xs py-0.5 border-b border-orange-50"><span className="text-slate-600 truncate mr-2">{s.name}</span><span className="text-slate-500 whitespace-nowrap">~{s.estimated_shops} | {s.distance_km}km</span></div>
                    ))}
                  </div>
                )}
                {scoutLayers.competitors.co_tenants?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs font-semibold text-slate-500">Co-Tenants:</div>
                    {scoutLayers.competitors.co_tenants.map((t, i) => (
                      <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-slate-600 truncate mr-2">{t.name} ({t.anchor})</span><span className="text-slate-500 whitespace-nowrap">{t.distance_km}km</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Facilities Card */}
            {scoutLayers.facilities && (
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-4 border border-blue-100" data-testid="facilities-summary-card">
                <div className="flex items-center gap-2 mb-2">
                  <Buildings size={16} className="text-blue-600" />
                  <h4 className="text-sm font-bold text-slate-700">Public Facilities</h4>
                </div>
                <div className="space-y-2">
                  {scoutLayers.facilities.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full" style={{background: FACILITY_COLORS[f.type] || '#6b7280'}}></span>
                      <span className="font-semibold text-slate-700 capitalize">{f.type}</span>
                      <span className="text-slate-400">—</span>
                      <span className="text-slate-600 truncate flex-1">{f.name}</span>
                      <span className="text-slate-500 whitespace-nowrap">{f.distance_km} km</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
